/**
 * "Jump to change" targets (W1-09 state-store; DESIGN.md §5.13, NAV-17).
 *
 * Undo and redo return a Txn whose `focus` tells the shell where to scroll and which line to activate. Commands
 * may set it explicitly (`tx.setFocus`, `TxnMeta.focus`); otherwise it is derived from the ops: the first op that
 * names a place in time and/or a line (notes, keys, tracks, lines, tempo points, markers, effects, videos) wins.
 * Lines are reported only when they exist in the document after the change (an undone "add line" has no line to
 * activate).
 */

import type { ChartDoc, LineId, TrackOwner } from '../core/model/types';
import type { Op, TxnFocus } from '../core/ops/types';
import type { Beat } from '../core/time/types';

function focusOf(beat: Beat | undefined, line: LineId | undefined): TxnFocus | null {
  if (beat === undefined && line === undefined) return null;
  return { ...(beat !== undefined ? { beat } : {}), ...(line !== undefined ? { line } : {}) };
}

function ownerLine(owner: TrackOwner | undefined, doc: ChartDoc): LineId | undefined {
  if (owner === undefined) return undefined;
  switch (owner.kind) {
    case 'line':
      return owner.line;
    case 'videoVar':
      return doc.videos.get(owner.video)?.attachLine ?? undefined;
    case 'effectVar':
      return undefined;
  }
}

function existing(line: LineId | undefined, doc: ChartDoc): LineId | undefined {
  return line !== undefined && doc.lines.has(line) ? line : undefined;
}

/** Focus of one op, resolved against the document AFTER the ops were applied. */
function opFocus(op: Op, doc: ChartDoc): TxnFocus | null {
  if (op.o === 'idSeq' || op.o === 'order' || op.o === 'single') return null;
  switch (op.tbl) {
    case 'notes': {
      const rec = op.o === 'set' ? doc.notes.get(op.id) : op.rec;
      if (rec === undefined) return null;
      return focusOf(rec.t, existing(rec.line, doc));
    }
    case 'keys': {
      const rec = op.o === 'set' ? doc.keys.get(op.id) : op.rec;
      if (rec === undefined) return null;
      return focusOf(rec.t, existing(ownerLine(doc.tracks.get(rec.track)?.owner, doc), doc));
    }
    case 'tracks': {
      const rec = op.o === 'set' ? doc.tracks.get(op.id) : op.rec;
      return focusOf(undefined, existing(ownerLine(rec?.owner, doc), doc));
    }
    case 'lines': {
      const id = op.o === 'set' ? op.id : op.rec.id;
      return focusOf(undefined, existing(id, doc));
    }
    case 'bpm': {
      const rec = op.o === 'set' ? doc.bpm.get(op.id) : op.rec;
      return focusOf(rec?.t, undefined);
    }
    case 'markers': {
      const rec = op.o === 'set' ? doc.markers.get(op.id) : op.rec;
      return focusOf(rec?.t, undefined);
    }
    case 'effects': {
      const rec = op.o === 'set' ? doc.effects.get(op.id) : op.rec;
      return focusOf(rec?.start, undefined);
    }
    case 'videos': {
      const rec = op.o === 'set' ? doc.videos.get(op.id) : op.rec;
      if (rec === undefined) return null;
      return focusOf(rec.time, existing(rec.attachLine ?? undefined, doc));
    }
    case 'groups':
    case 'generators':
    case 'assets':
      return null;
  }
}

/**
 * The jump-to-change target of `ops` (applied; `doc` is the document after them). A beat-only target is completed
 * with the first line found later in the ops, so moving notes reports both where and on which line.
 */
export function deriveFocus(ops: readonly Op[], doc: ChartDoc): TxnFocus | null {
  let found: TxnFocus | null = null;
  for (const op of ops) {
    const f = opFocus(op, doc);
    if (f === null) continue;
    if (found === null) {
      found = f;
      if (f.beat !== undefined && f.line !== undefined) return found;
      continue;
    }
    if (found.beat === undefined && f.beat !== undefined) found = { ...found, beat: f.beat };
    if (found.line === undefined && f.line !== undefined) found = { ...found, line: f.line };
    if (found.beat !== undefined && found.line !== undefined) return found;
  }
  return found;
}
